/* eslint-disable no-new */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  Card,
  Stack,
  FormControlLabel,
  Switch,
  Skeleton,
  useMediaQuery,
  useTheme,
  FormHelperText
} from '@mui/material';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axios, { getErrorMessage } from 'utils/axios';
import { FormProvider, RHFTextField } from 'components/hook-form';
import { LoadingButton } from '@mui/lab';
import CRUD from 'utils/Crud';
import useIsMountedRef from 'hooks/useIsMountedRef';

// const NewUserSchema = Yup.object().shape({
//   title: Yup.string().required('Field is required'),
//   password: Yup.string().required('Field is required'),
//   url: Yup.string().url('Must be a valid URL').required('Field is required'),
//   external: Yup.boolean().required('Field is required'),
// });

const urlWithTemplatesRegex = /^(https?:\/\/[^\s{}]*)(\{[a-zA-Z0-9_]+\})?([^\s{}]*)$/;

// Custom test function to validate URL allowing {template_strings}
const isValidUrlWithTemplates = (value) => {
  if (!value) return false;
  // Replace placeholders with a safe dummy string to validate URL parts
  const replaced = value.replace(/\{[a-zA-Z0-9_]+\}/g, 'placeholder');

  try {
    // Using URL constructor to check validity (requires full URL)
    new URL(replaced);
    return true;
  } catch {
    return false;
  }
};

const NewUserSchema = Yup.object().shape({
  title: Yup.string().required('Field is required'),
  password: Yup.string().required('Field is required'),
  url: Yup.string()
    .required('Field is required')
    .test(
      'is-valid-url-with-templates',
      'Must be a valid URL and can include template strings like {user_id}',
      value => isValidUrlWithTemplates(value)
    ),
  external: Yup.boolean().required('Field is required'),
});

AddFeaturedBlogPostsModal.propTypes = {
  open: PropTypes.bool.isRequired   ,
  isEdit: PropTypes.bool.isRequired ,
  handleClose: PropTypes.func.isRequired,
  handleComplete: PropTypes.func.isRequired,
  data: PropTypes.object,
};

export default function AddFeaturedBlogPostsModal({
  open,
  handleClose,
  handleComplete,
  data,
  isEdit,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues: {
      title: data?.title || '',
      url: data?.url || '',
      password: '',
      external: data?.external ?? false, // default false for switch boolean
    },
  });

  const {
    reset,
    watch,
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values) => {
    try {
      if (isEdit) {
        values.id = data.id;
        await axios.post(`${CRUD.appContent.featuredPosts}/update`, values);
      } else {
        await axios.post(`${CRUD.appContent.featuredPosts}`, values);
      }
      if (isMountedRef.current) {
        reset();
        enqueueSnackbar(isEdit ? 'Update success!' : 'Create success!');
        handleComplete();
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="featured-post-modal-title"
      aria-describedby="featured-post-modal-description"
      hideBackdrop={false}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: fullScreen ? '90%' : 600,
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: fullScreen ? 3 : 4,
          border: '1px solid #eee',
        }}
      >
        <Typography
          id="featured-post-modal-title"
          variant="h6"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{ fontWeight: 'bold' }}
        >
          {!isEdit ? 'Add InApp Link' : 'Update InApp Link'}
        </Typography>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Card
              sx={{
                p: 3,
                mt: 2,
                borderRadius: 2,
                boxShadow: 'none',
                bgcolor: '#fafafa',
              }}
            >
              <RHFTextField
                name="title"
                label="Title"
                fullWidth
                sx={{ mb: 3 }}
                autoFocus
                autoComplete="off"
              />
              <RHFTextField
                name="url"
                label="URL"
                fullWidth
                sx={{ mb: 1 }}
                autoComplete="off"
                placeholder="https://example.com/your-link"
                helperText="You can use {user_id} and {token} placeholders for personalized URLs."
              />

              {/* External Browser Switch */}
              <Controller
                name="external"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Open link in external browser"
                    sx={{ mt: 2 }}
                  />
                )}
              />
              <FormHelperText sx={{ mb: 2, ml: 1 }}>
                Enable this option to open the link in an external browser tab or app.
              </FormHelperText>

              <RHFTextField
                name="password"
                label="Confirm Password"
                type="password"
                required
                sx={{ mb: 2, mt: 1 }}
                />

              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 4 }}>
                <LoadingButton
                  type="button"
                  variant="outlined"
                  onClick={handleClose}
                  sx={{ mr: 2}}
                  size={'large'}
                >
                  Cancel 
                </LoadingButton>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  size={'large'}
                >
                  Save {!isEdit ? '' : 'Changes'}
                </LoadingButton>
              </Stack>
            </Card>
        </FormProvider>
      </Box>
    </Modal>
  );
}
